/**
 * Content for the About, Privacy, Terms and Cookie pages.
 *
 * Every statement here was checked against the code and the live site on
 * 2026-10-18 (see the Phase B audit). Anything not verifiable — legal entity,
 * retention periods, refund terms, licence terms — is left out and flagged
 * with `review`, which renders as a visible "For review" note until removed.
 *
 * Inline syntax (rendered without HTML): [text](/path) for links; the support
 * address is linked automatically.
 *
 * Block types: { p }, { list: [...] }, { table: { head: [...], rows: [[...]] } }.
 */

export const SUPPORT_EMAIL = 'info@ivisora.co';
const UPDATED = '2026-10-18';

const REVIEW_ENTITY = 'iVisora is not yet a registered legal entity. Before launch, name the person or company legally responsible for the service, and decide whether a Grievance Officer must be named (Information Technology Rules / Digital Personal Data Protection Act, India).';

export const ABOUT = {
  slug: 'about',
  title: 'About iVisora',
  description: 'iVisora is an online design editor with ready-made templates, a creator marketplace and AI tools.',
  lastUpdated: UPDATED,
  draft: false,
  intro: [
    'iVisora is an online design editor. Pick a template or start from a blank page, make it yours, and download it — all in the browser, with nothing to install.',
  ],
  features: [
    { title: 'A full editor in your browser', body: 'Text, shapes and elements, photos, backgrounds, QR codes and charts on designs of one or many pages.' },
    { title: 'Templates to start from', body: 'Browse the template gallery, open one in the editor and change anything on it.' },
    { title: 'Your brand, kept handy', body: 'Keep your brand colours and assets in a brand kit and upload your own images.' },
    { title: 'Download in common formats', body: 'Export your designs as PNG, JPG or PDF.' },
    { title: 'Work with others', body: 'Invite people to a design as editors or viewers, or share a link to it.' },
    { title: 'AI tools', body: 'Generate images and text with AI, powered by OpenAI.' },
    { title: 'A marketplace for creators', body: 'Designers can publish templates — free or paid — for others to use.' },
  ],
  sections: [
    {
      id: 'who-its-for',
      heading: 'Who it’s for',
      blocks: [
        { p: 'iVisora is built for creators, designers, editors and content teams who need good-looking graphics without a steep learning curve.' },
      ],
    },
    {
      id: 'plans',
      heading: 'Plans',
      blocks: [
        { p: 'You can start for free. Paid plans add more — see [Pricing](/pricing) for what each plan includes.' },
      ],
    },
  ],
  review: 'Optional: add the story of who is behind iVisora (founder or team) — nothing has been invented here.',
};

export const PRIVACY = {
  slug: 'privacy',
  title: 'Privacy Policy',
  description: 'What information iVisora collects, how it is used, who it is shared with, and the choices you have.',
  lastUpdated: UPDATED,
  draft: true,
  intro: [
    'This policy explains what information iVisora collects when you use ivisora.co, why, who we share it with, and the choices you have. If anything is unclear, email info@ivisora.co.',
  ],
  sections: [
    {
      id: 'who-we-are',
      heading: 'Who we are',
      blocks: [
        { p: 'iVisora (“iVisora”, “we”, “us”) runs ivisora.co, an online design editor with templates, a creator marketplace and AI tools. You can reach us at info@ivisora.co.' },
      ],
      review: REVIEW_ENTITY,
    },
    {
      id: 'information-we-collect',
      heading: 'Information we collect',
      blocks: [
        { p: '**Your account.** Your name, email address and password when you sign up. We store your password only as a one-way hash (bcrypt), never as plain text. We also keep your plan and, if you add one, your profile photo.' },
        { p: '**Your content.** The designs you create, the images and files you upload, and templates you create or submit to the marketplace.' },
        { p: '**Creators.** If you sell templates: your public creator profile, your payout method (PayPal or bank transfer), payout email, bank name and the last four digits of your account number, and records of your sales and earnings.' },
        { p: '**Payments.** Payments are handled by Stripe on Stripe’s own checkout page. We receive and store the outcome — your plan, subscription status, amounts and Stripe reference numbers. We never receive or store your full card details.' },
        { p: '**Messages you send us.** When you use the [contact form](/contact): your name, email, topic, company (if given) and message, plus a scrambled (hashed) form of your IP address that helps us block abuse. We do not store the IP address itself.' },
        { p: '**People you invite.** The email addresses of people you invite to collaborate on a design or template.' },
        { p: '**How you use iVisora.** Which templates you view, use, copy or download, linked to your account when you are signed in. We use this for template statistics and to understand which templates lead people to upgrade.' },
        { p: '**Analytics.** We use PostHog to understand how the site is used — see [Analytics and session recordings](#analytics).' },
        { p: '**Technical information.** Like most websites, our servers receive technical information with every request, such as your IP address, browser type and the page requested.' },
      ],
      review: 'Confirm how long server logs are kept and what they contain on the production server.',
    },
    {
      id: 'analytics',
      heading: 'Analytics and session recordings',
      blocks: [
        { p: 'We use PostHog, an analytics service whose servers are in the United States. It records the pages you visit, page-load performance (“web vitals”), and general information about your browser and device. We don’t tell PostHog who you are: we don’t send it your name or email address, and security codes in web addresses — such as those in password-reset and invitation links — are removed before anything is sent.' },
        { p: 'PostHog also records a small sample of visits — about 1 in 100 that last longer than 30 seconds — as session recordings: a replay of what appeared on screen and how you moved through the site, together with technical browser console messages. What you type into form fields is hidden in these recordings, but other text shown on the page — which can include your name or email address — may be visible in them. We use recordings only to find and fix problems.' },
        { p: 'PostHog stores an identifier in a cookie and in your browser’s storage so it can recognise repeat visits. See the [Cookie Policy](/cookies) for details and how to block it.' },
      ],
      review: 'Decide whether to keep session recordings, and whether analytics and recordings need consent (opt-in) or an opt-out before launch. The site currently has no cookie or consent banner.',
    },
    {
      id: 'how-we-use-it',
      heading: 'How we use information',
      blocks: [
        { list: [
          'To run iVisora: your account, saving and opening your designs, sharing, collaboration and downloads.',
          'To process payments, subscriptions and creator payouts.',
          'To answer messages you send us.',
          'To send emails the service needs, such as password-reset links and collaboration invitations.',
          'To keep iVisora secure and prevent abuse.',
          'To understand how iVisora is used and improve it.',
          'To meet legal obligations.',
        ] },
        { p: 'We do not currently send marketing emails. We do not sell your personal information.' },
      ],
    },
    {
      id: 'service-providers',
      heading: 'Who we share it with',
      blocks: [
        { p: 'We use these services to run iVisora. Each receives only what it needs for its job.' },
        { table: {
          head: ['Service', 'What it does for us', 'What it receives'],
          rows: [
            ['Amazon Web Services', 'Hosts our servers and database in Mumbai, India; sends collaboration-invitation emails', 'All the information described above (hosting); invitee email addresses (email)'],
            ['Stripe', 'Payments and subscriptions', 'What you enter on Stripe’s checkout page; the plan or template being bought'],
            ['OpenAI', 'AI image and text generation', 'The prompts and content you submit to AI features'],
            ['PostHog (United States)', 'Analytics and session recordings', 'Usage, device and performance information; recordings of a sample of visits'],
            ['Pexels, Pixabay, Unsplash', 'Stock photo search', 'Your search terms (sent from our server); your browser loads the photos you see from them'],
            ['Google Fonts', 'Fonts', 'Your browser contacts Google to load fonts, which reveals your IP address to Google'],
            ['Our email provider', 'Password-reset emails and contact-form notifications', 'Your email address and the message being sent'],
          ],
        } },
        { p: 'We may also disclose information if the law requires it, or to protect the rights and safety of our users and of iVisora.' },
      ],
      review: 'Name the email (SMTP) provider used in production, and confirm Amazon SES is set up there (invitation emails are only sent if it is). If Cloudinary image hosting is switched on in production, add it to this list.',
    },
    {
      id: 'where-its-stored',
      heading: 'Where your information is stored',
      blocks: [
        { p: 'Our servers and database are hosted by Amazon Web Services in Mumbai, India. Some of the services above — such as PostHog, Stripe and OpenAI — process information in other countries, including the United States.' },
      ],
      review: 'Check what Indian law requires for transfers of personal data outside India.',
    },
    {
      id: 'public-content',
      heading: 'What others can see',
      blocks: [
        { list: [
          'Your designs are private unless you share them. A share link lets anyone who has the link view that design.',
          'People you invite to a design can see it and, if they are editors, change it.',
          'Templates published in the gallery or marketplace, and creator profiles, are public.',
          'Images and files you upload are stored at web addresses that anyone who has the address can open. Don’t upload anything you need to keep secret.',
        ] },
      ],
    },
    {
      id: 'retention',
      heading: 'How long we keep information',
      blocks: [
        { p: 'We keep your account and your content for as long as your account exists. Messages sent through the contact form are kept until we delete them. You can ask us to delete your information at any time — see below.' },
      ],
      review: 'Set and state retention periods (accounts after closure, server logs, analytics, contact messages, payment records kept for tax law).',
    },
    {
      id: 'your-choices',
      heading: 'Your choices and rights',
      blocks: [
        { p: 'You can see and change most of your account information in your account settings. To get a copy of your information, correct it, or have your account and content deleted, email info@ivisora.co from the address on your account. Deleting an account is handled by our team on request; there is not yet a button for it in the app.' },
        { p: 'You can block or delete cookies and site storage in your browser settings — see the [Cookie Policy](/cookies).' },
        { p: 'If you are in India, the Digital Personal Data Protection Act, 2023 gives you rights over your personal data, including to access, correct and erase it and to have grievances addressed. Contact us to use them.' },
      ],
      review: 'Have a lawyer confirm what the Digital Personal Data Protection Act and its rules require here (notice content, consent, response times, grievance officer, nomination), and that every promise in this section can be met in practice.',
    },
    {
      id: 'security',
      heading: 'Security',
      blocks: [
        { p: 'The site is served over HTTPS, passwords are stored only as one-way hashes, and payment details stay with Stripe. Access to the admin tools is restricted to authorised staff. No system is completely secure, so please use a strong, unique password.' },
      ],
    },
    {
      id: 'children',
      heading: 'Children',
      blocks: [
        { p: 'iVisora is not directed at children.' },
      ],
      review: 'Decide the minimum age for an account. Under the Digital Personal Data Protection Act, anyone under 18 is a child and needs verifiable parental consent.',
    },
    {
      id: 'changes',
      heading: 'Changes to this policy',
      blocks: [
        { p: 'If we change this policy, we will update the date at the top of this page. If a change is significant, we will tell you more prominently, for example by email or with a notice on the site.' },
      ],
    },
    {
      id: 'contact',
      heading: 'Contact',
      blocks: [
        { p: 'Questions or requests about your information: info@ivisora.co, or use the [contact form](/contact) and choose “Privacy and data requests”.' },
      ],
      review: REVIEW_ENTITY,
    },
  ],
};

export const COOKIES = {
  slug: 'cookies',
  title: 'Cookie Policy',
  description: 'The cookies and browser storage iVisora uses, what each one is for, and how to control them.',
  lastUpdated: UPDATED,
  draft: true,
  intro: [
    'Cookies and browser storage are small pieces of information a website keeps in your browser. This page lists what iVisora keeps there and why. It reflects what the site actually stored when we checked it on 18 October 2026.',
  ],
  sections: [
    {
      id: 'summary',
      heading: 'In short',
      blocks: [
        { list: [
          'iVisora’s own code does not set cookies. It uses your browser’s storage to keep you signed in and to save your work and preferences on your device.',
          'One cookie is set on ivisora.co: by PostHog, our analytics service.',
          'Stripe sets its own cookies on its checkout page, which is on Stripe’s website.',
        ] },
      ],
    },
    {
      id: 'essential',
      heading: 'Storage the site needs to work',
      blocks: [
        { p: 'These are kept in your browser’s local storage. They stay until you sign out, clear them, or they are replaced.' },
        { table: {
          head: ['Name', 'Purpose'],
          rows: [
            ['ivisora_auth_token, ivisora_user', 'Keep you signed in (your sign-in token and basic account details: name, email, plan)'],
            ['user_token, user · admin_token, admin_user', 'The same, for the designer portal and the admin portal'],
            ['ivisora_autosave_…', 'Unsaved editor work, kept on your device so a refresh doesn’t lose it'],
            ['designs', 'Designs you saved while signed out (they exist only in this browser)'],
            ['ivisora_brand_kit, ivisora_custom_frames_…, designhub:recentSizes', 'Your brand kit, frames you saved, and recently used canvas sizes'],
            ['ivisora_prefs, contentpack_onboarded_v1', 'Your account-settings preferences, and whether you have seen an introduction screen'],
            ['ivisora_avatar_photo, ivisora_avatar_gradient, ivisora_activity', 'How your profile picture looks, and a short list of your recent actions shown on your dashboard'],
            ['ivisora_pending_invite_token', 'An invitation you opened before signing in, so it can be accepted afterwards'],
          ],
        } },
      ],
    },
    {
      id: 'analytics',
      heading: 'Analytics',
      blocks: [
        { table: {
          head: ['Name', 'Set by', 'Type', 'Purpose', 'Expires'],
          rows: [
            ['ph_phc_…_posthog', 'PostHog', 'Cookie (ivisora.co) and local storage', 'A random identifier so PostHog can tell repeat visits apart, and settings for the current visit', '1 year'],
            ['ph_phc_…_window_id, …_primary_window_exists, …_session_registered_properties', 'PostHog', 'Session storage', 'Track the current visit and browser tab', 'When you close the tab'],
          ],
        } },
        { p: 'Read [Analytics and session recordings](/privacy#analytics) in the Privacy Policy for what PostHog records.' },
      ],
      review: 'The site has no consent banner or opt-out control. Decide whether analytics cookies need consent before launch; if so, add a banner that can turn PostHog off.',
    },
    {
      id: 'third-parties',
      heading: 'Other websites',
      blocks: [
        { p: 'When you pay, you are taken to Stripe’s checkout page, where Stripe uses its own cookies, mainly for security and fraud prevention. Stripe’s cookie policy applies there.' },
        { p: 'Fonts are loaded from Google Fonts. We did not observe Google setting cookies, but your browser does contact Google’s servers.' },
      ],
    },
    {
      id: 'control',
      heading: 'How to control them',
      blocks: [
        { list: [
          'Signing out removes your sign-in token from the browser.',
          'Your browser settings let you see, delete and block cookies and site data for ivisora.co. Blocking PostHog does not stop iVisora working; blocking all site storage will stop you staying signed in.',
          'Browser extensions that block trackers usually block PostHog.',
        ] },
      ],
    },
    {
      id: 'contact',
      heading: 'Contact',
      blocks: [
        { p: 'Questions about cookies: info@ivisora.co.' },
      ],
    },
  ],
};

export const TERMS = {
  slug: 'terms',
  title: 'Terms of Service',
  description: 'The terms for using iVisora: accounts, plans and payments, your content, templates, the marketplace and AI features.',
  lastUpdated: UPDATED,
  draft: true,
  intro: [
    'These terms apply when you use ivisora.co. By creating an account or using iVisora, you agree to them. Please also read our [Privacy Policy](/privacy).',
  ],
  sections: [
    {
      id: 'about',
      heading: 'About these terms',
      blocks: [
        { p: 'iVisora (“iVisora”, “we”, “us”) provides an online design editor, templates, a creator marketplace and AI tools (together, the “service”).' },
      ],
      review: REVIEW_ENTITY,
    },
    {
      id: 'accounts',
      heading: 'Your account',
      blocks: [
        { list: [
          'Give accurate information when you sign up and keep it up to date.',
          'Keep your password secret. You are responsible for what happens under your account.',
          'Tell us at info@ivisora.co if you think someone else has used your account.',
        ] },
      ],
      review: 'State the minimum age to have an account (see the Children section of the Privacy Policy).',
    },
    {
      id: 'plans',
      heading: 'Plans and payments',
      blocks: [
        { p: 'You can use iVisora on a free plan. Paid plans and their prices are described on the [Pricing](/pricing) page. Payments are processed by Stripe.' },
        { p: 'A paid subscription renews for each billing period until it is cancelled. When you cancel, it stays active until the end of the period you have paid for.' },
        { p: 'Paid marketplace templates are bought individually.' },
      ],
      review: 'Add the refund policy, how prices and taxes (GST) are shown and charged, and what happens to paid features and content when a plan ends. None of these is defined yet.',
    },
    {
      id: 'your-content',
      heading: 'Your content',
      blocks: [
        { p: 'You keep ownership of the designs and files you create and upload. You give iVisora permission to store, process, copy and display them only as needed to run the service for you — for example to save, show, share and export them as you ask.' },
        { p: 'You are responsible for having the rights to everything you upload and use, including images, fonts and logos.' },
      ],
    },
    {
      id: 'templates',
      heading: 'Templates',
      blocks: [
        { p: 'Templates help you start a design. Free-plan users can customise and download templates within their plan’s limits; Pro users can also save their own copies.' },
      ],
      review: 'Write the template licence: what customers may do with designs made from templates (personal and commercial use, resale, trademarks), and which photos in templates come from stock providers under their own licences.',
    },
    {
      id: 'marketplace',
      heading: 'Selling templates as a creator',
      blocks: [
        { p: 'Creators can submit templates to the marketplace. Every submission is reviewed before it is published. When a paid template sells, the creator earns a share of the price and iVisora keeps a commission; earnings are paid out to the payout method in the creator’s profile.' },
        { p: 'Creators must own, or have permission to use, everything in the templates they submit.' },
      ],
      review: 'Publish the commission rate, payout schedule and minimum, refund impact on earnings, and the licence creators grant to iVisora and to buyers.',
    },
    {
      id: 'ai',
      heading: 'AI features',
      blocks: [
        { p: 'Some features generate images and text with AI, powered by OpenAI. What you submit is sent to OpenAI to produce the result. AI output can be inaccurate or similar to existing work; check it before you rely on or publish it.' },
      ],
    },
    {
      id: 'acceptable-use',
      heading: 'Acceptable use',
      blocks: [
        { p: 'Do not use iVisora to:' },
        { list: [
          'break the law or infringe anyone’s rights, including copyright and trademarks;',
          'create or share content that is hateful, harassing, sexually exploitative, or that promotes violence;',
          'upload malware, or try to break, overload or get around the security or access controls of the service (for example to use paid templates without buying them);',
          'scrape or copy the service or its templates in bulk, or resell access to it;',
          'send spam or impersonate someone else.',
        ] },
        { p: 'We may remove content or suspend accounts that break these rules.' },
      ],
    },
    {
      id: 'third-party',
      heading: 'Other services',
      blocks: [
        { p: 'Parts of iVisora rely on other companies, such as Stripe for payments, OpenAI for AI features and Pexels, Pixabay and Unsplash for stock photos. Their own terms apply to their services, and stock photos are subject to their providers’ licences.' },
      ],
    },
    {
      id: 'ending',
      heading: 'Ending your use',
      blocks: [
        { p: 'You can stop using iVisora at any time, and ask us to delete your account by emailing info@ivisora.co. We may suspend or close accounts that break these terms.' },
      ],
    },
    {
      id: 'liability',
      heading: 'Disclaimers and limitation of liability',
      blocks: [],
      review: 'To be written with a lawyer. No warranty disclaimer or limitation of liability has been drafted, on purpose.',
    },
    {
      id: 'law',
      heading: 'Governing law',
      blocks: [
        { p: 'These terms are governed by the laws of India.' },
      ],
      review: 'Subject to legal review. Decide the courts with jurisdiction (city), and whether disputes go to arbitration.',
    },
    {
      id: 'changes',
      heading: 'Changes to these terms',
      blocks: [
        { p: 'If we change these terms, we will update the date at the top of this page and, for significant changes, tell you in advance, for example by email or with a notice on the site.' },
      ],
    },
    {
      id: 'contact',
      heading: 'Contact',
      blocks: [
        { p: 'Questions about these terms: info@ivisora.co.' },
      ],
    },
  ],
};
